import * as S from "@/containers/Project/components/styled";
import {
  flattenProjectScheduleMilestones,
  projectHasRejectedMilestone,
} from "@/containers/Project/helper";
import { CommentTarget, ProjectMilestoneStatus } from "@/enums";
import ProjectMilestoneStatusActions from "@/enums/ProjectMilestoneStatusActions";
import { useMobileLayout } from "@/hooks/useAppMedia";
import { useProjectContext } from "@/providers";
import { useGetTagsQuery } from "@/services";
import * as GlobalS from "@/sharedStyled";
import { Form, Input, Typography } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import {
  displayDescription,
  getActionFromStatus,
  milestoneWillCompleteProject,
  shouldMilestoneCompleteProject,
} from "./helper";

const { Title } = Typography;
const { TextArea } = Input;

const MilestoneStatusActionCommentModal = () => {
  const intl = useIntl();

  const {
    milestoneStatusActionCommentModal,
    setMilestoneStatusActionCommentModal,
    milestoneStatusChange,
    updatingMilestoneStatus,
    project,
  } = useProjectContext();

  const [form] = Form.useForm();
  const isMobileLayout = useMobileLayout();

  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const milestoneLoaded =
    milestoneStatusActionCommentModal?.milestoneId && selectedMilestone;
  const [tagId, setTagId] = useState(null);

  const { data: tags } = useGetTagsQuery();

  const milestoneModalKey = milestoneStatusActionCommentModal?.milestoneId
    ? `${milestoneStatusActionCommentModal.milestoneId}|${milestoneStatusActionCommentModal.targetStatus}|${milestoneStatusActionCommentModal.finalApprover}`
    : null;

  useEffect(() => {
    if (!milestoneModalKey || !milestoneLoaded) {
      return;
    }
    form.resetFields();
  }, [milestoneModalKey, milestoneLoaded, form]);

  useEffect(() => {
    if (
      project?.schedules?.length > 0 &&
      milestoneStatusActionCommentModal?.milestoneId
    ) {
      const flattenedMilestones = flattenProjectScheduleMilestones(project);

      const milestone = flattenedMilestones.find(
        (flatMilestone) =>
          flatMilestone.id === milestoneStatusActionCommentModal.milestoneId
      );
      setSelectedMilestone(milestone);
    }
  }, [milestoneStatusActionCommentModal?.milestoneId, project]);

  useEffect(() => {
    if (!milestoneLoaded || !tagId) {
      return;
    }
    form.setFieldsValue({
      tag: tagId,
    });
  }, [tagId, milestoneLoaded, form]);

  useEffect(() => {
    var label = milestoneStatusActionCommentModal.targetStatus;
    if (label === ProjectMilestoneStatus.Reenable) {
      label = "Re-enabled";
    }
    if (tags?.length) {
      const targetTag = tags.find(
        (tag) =>
          tag.targets?.includes(CommentTarget.Milestone) && tag.label === label
      );
      setTagId(targetTag?.id);
    }
  }, [tags, milestoneStatusActionCommentModal]);

  const completeProjectWithRejectedMilestone = useCallback(() => {
    const flattenedMilestones = flattenProjectScheduleMilestones(project);

    return (
      shouldMilestoneCompleteProject(
        flattenedMilestones,
        milestoneStatusActionCommentModal?.milestoneId
      ) && projectHasRejectedMilestone(project)
    );
  }, [milestoneStatusActionCommentModal?.milestoneId, project]);

  const handleOk = () => {
    form.validateFields().then(({ text }) => {
      const commentData = text
        ? {
            tag: tagId,
            text: text,
          }
        : null;
      handleSave(commentData);
    });
  };

  const handleSave = (comment) => {
    milestoneStatusChange(
      project.id,
      milestoneStatusActionCommentModal.milestoneId,
      getActionFromStatus(milestoneStatusActionCommentModal.targetStatus),
      comment,
      form
    );
  };

  const handleCancel = () => {
    form.resetFields();
    setMilestoneStatusActionCommentModal({
      milestoneId: null,
      targetStatus: null,
      finalApprover: false,
    });
    setSelectedMilestone(null);
  };

  const commentRequiredForStatus = (action) => {
    if (
      completeProjectWithRejectedMilestone() &&
      action === ProjectMilestoneStatus.Approved
    ) {
      return true;
    }

    return (
      action === ProjectMilestoneStatus.Skipped ||
      action === ProjectMilestoneStatus.Rejected ||
      action === ProjectMilestoneStatus.Released ||
      action === ProjectMilestoneStatus.InApproval ||
      action === ProjectMilestoneStatus.Reenable
    );
  };

  const renderDescriptionAndWarning = (action) => {
    const intlBaseId = "milestoneStatusActionCommentModal.";
    const willCompleteProject = milestoneWillCompleteProject(
      action,
      selectedMilestone.shouldCompleteProject,
      milestoneStatusActionCommentModal.finalApprover
    );
    const displayWarning = action === ProjectMilestoneStatusActions.Skip;

    if (
      completeProjectWithRejectedMilestone() &&
      (action === ProjectMilestoneStatusActions.Approve ||
        action === ProjectMilestoneStatusActions.Release ||
        action === ProjectMilestoneStatusActions.SubmitForApproval)
    ) {
      action = "override";
    }

    return (
      <>
        {displayDescription(action) && (
          <S.ModalDescription>
            {intl.formatMessage({
              id: intlBaseId + `${action.toLowerCase()}Description`,
            })}
          </S.ModalDescription>
        )}
        {displayWarning && (
          <S.ModalAlert
            type="warning"
            message={intl.formatMessage({
              id: intlBaseId + `${action.toLowerCase()}Warn`,
            })}
          />
        )}
        {willCompleteProject && (
          <S.ModalAlert
            type="error"
            message={
              <span>
                <strong>
                  {intl.formatMessage({
                    id: "general.warning",
                  })}
                </strong>
                {intl.formatMessage({
                  id: "milestoneCompleteModal.content",
                })}
              </span>
            }
          />
        )}
      </>
    );
  };

  if (!milestoneLoaded) {
    return <Form form={form} component={false} />;
  }

  return (
    <GlobalS.ResponsiveModal
      $mobile={isMobileLayout}
      maskClosable={false}
      title={
        <Title level={4}>
          {intl.formatMessage({
            id: `milestoneStatusActionCommentModal.${getActionFromStatus(
              milestoneStatusActionCommentModal.targetStatus
            ).toLowerCase()}Title`,
          }) + ` ${selectedMilestone.displayName || selectedMilestone.name}`}
        </Title>
      }
      onOk={handleOk}
      onCancel={handleCancel}
      open={!!milestoneStatusActionCommentModal.milestoneId}
      okButtonProps={{ loading: updatingMilestoneStatus }}
      okText={intl.formatMessage({
        id: `milestoneStatusActionCommentModal.${getActionFromStatus(
          milestoneStatusActionCommentModal.targetStatus
        ).toLowerCase()}Title`,
      })}
    >
      {renderDescriptionAndWarning(
        getActionFromStatus(milestoneStatusActionCommentModal.targetStatus)
      )}

      <Form form={form} layout="vertical" name="comment-form">
        <Form.Item
          name="text"
          label={intl.formatMessage({ id: "comments.comment" })}
          rules={[
            {
              required: commentRequiredForStatus(
                milestoneStatusActionCommentModal.targetStatus
              ),
              message: intl.formatMessage({ id: "comments.noValue" }),
            },
          ]}
        >
          <TextArea rows={4} />
        </Form.Item>
      </Form>
    </GlobalS.ResponsiveModal>
  );
};

export default MilestoneStatusActionCommentModal;
